import { roundTo } from '../math/format';
import type { Option, Rng } from './types';

export interface Candidate {
  readonly label: string;
  readonly trap: string;
  /**
   * The numeric size this label represents, when the label is not already a
   * canonical rendering of it. Supply it whenever two different-looking labels
   * could mean the same thing — "1/8" and "2/16" are the same drill.
   */
  readonly value?: number;
}

/** Four options total. Fewer is fine; more is a wall of text. */
const MAX_OPTIONS = 4;

/**
 * Shuffle a correct answer in among its distractors and report where it landed.
 *
 * Three jobs, all load-bearing:
 *
 * 1. Shuffling. The artifact prototype had the correct answer at index 0 in all
 *    27 of its multiple-choice questions — a pattern a bored brain finds long
 *    before it learns the trade math.
 *
 * 2. De-duplication, by value where the caller gives one and by label
 *    otherwise. Distractors are computed from the same parameters as the
 *    answer, so some parameter combinations collapse one onto another. Two
 *    options that mean the same thing make a question unanswerable rather than
 *    hard — and "1/8" against "2/16" is unanswerable in a way that punishes
 *    exactly the student who understood the material.
 *
 * 3. Capping the count. Pass more candidates than you need; the survivors are
 *    taken in order until the list is full.
 */
export function mcOptions(
  rng: Rng,
  correct: string | { label: string; value: number },
  distractors: readonly Candidate[],
): { options: Option[]; answerIndex: number } {
  const answer: { label: string; value?: number } =
    typeof correct === 'string' ? { label: correct } : correct;

  /*
   * Both keys are tracked, and a match on either is a collision.
   *
   * Keying on one or the other is not enough: a distractor given a `value`
   * would be compared by value against an answer compared by label, and the
   * two would never meet — which is exactly how "12.566 sq in" once appeared
   * twice on the same question.
   */
  const seenLabels = new Set([answer.label]);
  const seenValues = new Set(answer.value === undefined ? [] : [roundTo(answer.value, 9)]);
  const kept: Option[] = [{ label: answer.label }];

  for (const d of distractors) {
    if (kept.length >= MAX_OPTIONS) break;
    if (seenLabels.has(d.label)) continue;
    const value = d.value === undefined ? undefined : roundTo(d.value, 9);
    if (value !== undefined && seenValues.has(value)) continue;

    seenLabels.add(d.label);
    if (value !== undefined) seenValues.add(value);
    kept.push({ label: d.label, trap: d.trap });
  }

  if (kept.length < 2) {
    throw new Error(
      `Every distractor collided with the answer "${answer.label}" — the question has only one option.`,
    );
  }

  const options = rng.shuffle(kept);
  return { options, answerIndex: options.findIndex((o) => o.trap === undefined) };
}
